import { Icon } from "@mui/material";
import { UserContext } from "App";
import MDBox from "components/MDBox";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import AppointmentsApi from "../../../../Classes/API/Appointments/AppointmentsApi";
import SearchStringOrIntReq from "../../../../Classes/Models/Requests/SearchStringOrIntReq";
import AccSessionDiv from "./AccSessionDiv";

const AppHourCell2 = ({
  AppHour,
  setSelectedAppHour,
  LoadReminders,
  AppDay,
  GetAppDayById,
  EnableFacilityModal,
  EnableAppointmentModal,
  setMovingSession,
  PasteSession,
  setIsLoaded,
  Reminders,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ReminderColor, setReminderColor] = useState("");
  const [ShowReminders, setShowReminders] = useState(false);

  let User = useContext(UserContext);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const targetId = queryParams.get("targetId");

    if (targetId) {
      const element = document.getElementById(targetId);

      if (element) {
        const timer = setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
            inline: "center",
          });
        }, 300);
        return () => clearTimeout(timer);
      }
    }
  }, [location.search, AppHour]);

  useEffect(() => {
    GetReminderSettings();
  }, [AppHour, Reminders]);

  const GetReminderSettings = () => {
    let reminders = Reminders.filter((x) => x.appointmentHourId == AppHour.id);
    if (reminders.length == 0) {
      return;
    }
    let reminderColor = "";
    for (let i = 0; i < reminders.length; i++) {
      let reminder = reminders[i];
      let patient = reminder.patient;
      if (patient.countSinceLastEval >= patient.lastEvalReminder) {
        reminderColor = "red";
        break;
      }
    }
    setReminderColor(reminderColor);
  };

  const GoToAccSessionDetails = (AccSession) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("targetId", "AccSessionDiv" + AccSession.id);
    window.history.replaceState(
      null,
      "",
      `${location.pathname}?${searchParams.toString()}`,
    );

    navigate("/AccountingSessions/Details", {
      state: {
        SearchSession: AccSession,
      },
    });
  };

  const DeleteAppointment = async (_accSess, isDummy) => {
    if ((!["Admin", "Owner", "HeadAccounting"].includes(User.userType)) && User.userType != "SubAdmin") {
      Swal.fire({
        title: "Invalid Permission",
        text: "You are not allowed to delete this appointment",
        icon: "error",
        confirmButtonText: "Ok",
      });
      return;
    }
    let DeleteAppointmentReq = new SearchStringOrIntReq();
    DeleteAppointmentReq.SearchInt = _accSess.id;
    DeleteAppointmentReq.IsDummy = isDummy;
    let confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (!confirmation.value) {
      return;
    }
    Swal.fire({
      title: "Deleting...",
      text: "Please wait...",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });
    let result =
      await AppointmentsApi.ClearAccountingSessionAppointmentHour(
        DeleteAppointmentReq,
      );
    if (result.status == "Success") {
      Swal.fire("Deleted!", "Your file has been deleted.", "success").then(
        () => {
          setIsLoaded(false);
          GetAppDayById(AppDay.id);
        },
      );
    } else {
      Swal.fire({
        title: "Error",
        text: result.message,
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  const handleAddMessage = async () => {
    const { value: message } = await Swal.fire({
      title: "Enter Message",
      input: "text",
      inputPlaceholder: "Type your message here...",
      showCancelButton: true,
      confirmButtonText: "Add",
      cancelButtonText: "Cancel",
    });

    if (message) {
      Swal.fire({
        title: "Creating Message...",
        text: "Please wait...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const requestBody = {
        AccountingSessionId: 0,
        AppointmentHourId: AppHour.id,
        IsDummy: true,
        PartitionId: null,
        BuildingId: null,
        PatientId: 0,
        PatientName: message,
        SessionName: "Message",
      };

      try {
        const res = await AppointmentsApi.LinkAccountingSessionToAppointmentHour(
          requestBody,
        );
        if (res.status === "Success") {
          Swal.fire("Success", "Message added successfully", "success").then(() => {
            setIsLoaded(false);
            GetAppDayById(AppDay.id);
          });
        } else {
          Swal.fire("Error", res.message, "error");
        }
      } catch (err) {
        Swal.fire("Error", "Something went wrong", "error");
      }
    }
  };

  const GetSessions = (AppHour) => {
    const realSessions = (AppHour.getListedAccountingSessions || []).map(
      (session) => ({
        ...session,
        isDummy: false,
      }),
    );

    const dummySessions = (AppHour.getListedAccountingSessionsDummy || []).map(
      (session) => ({
        ...session,
        isDummy: true,
      }),
    );

    return [...realSessions, ...dummySessions].map((session) => (
      <AccSessionDiv
        key={session.isDummy ? `dummy-${session.id}` : `real-${session.id}`}
        AccSession={session}
        isDummy={session.isDummy}
        GoToAccSessionDetails={GoToAccSessionDetails}
        DeleteAppointment={DeleteAppointment}
        EnableFacilityModal={EnableFacilityModal}
        setMovingSession={setMovingSession}
        setIsLoaded={setIsLoaded}
        AppHour={AppHour}
        AppDay={AppDay}
        GetAppDayById={GetAppDayById}
      />
    ));
  };

  const GetReminders = () => {
    let reminders = Reminders.filter((x) => x.appointmentHourId == AppHour.id);
    if (reminders.length == 0) {
      return [];
    }

    let Divs = [];
    for (let i = 0; i < reminders.length; i++) {
      let reminder = reminders[i];
      let patient = reminder.patient;
      if (patient.countSinceLastEval < patient.lastEvalReminder) {
        continue;
      }
      Divs.push(
        <div
          key={patient.id}
          className="d-flex justify-content-between"
          style={{ marginBottom: "5px", fontSize: "0.9em" }}
        >
          <span className="badge badge-danger">
            {patient.getUser.name} has had {patient.countSinceLastEval} sessions
            with no ReEval
          </span>
          <button
            className="btn btn-primary ml-3"
            onClick={(e) => {
              e.target.disabled = true;
              ResetCountSinceLastEval(patient);
            }}
          >
            <Icon>undo</Icon>
          </button>
        </div>,
      );
    }
    return Divs;
  };

  const ResetCountSinceLastEval = async (patient) => {
    let req = new SearchStringOrIntReq();
    req.SearchInt = patient.id;
    let result = await AppointmentsApi.ResetCountSinceLastEval(req);
    if (result.status == "Success") {
      Swal.fire({
        title: "Success",
        text: "Count Since Last Eval has been reset",
        icon: "success",
        confirmButtonText: "Ok",
      }).then(() => {
        LoadReminders();
      });
    } else {
      Swal.fire({
        title: "Error",
        text: result.message,
        icon: "error",
        confirmButtonText: "Ok",
      });
    }
  };

  return (
    <MDBox
      className={`AppHourCell`}
      sx={{
        border: `3px solid ${ReminderColor}`,
        borderRadius: "5px",
        padding: "5px",
        margin: "5px",
        "&:hover": {
          border: `5px solid ${ReminderColor}`,
        },
      }}
    >
      <div>{GetSessions(AppHour)}</div>
      <div className="d-flex justify-content-between">
        {setSelectedAppHour && (
          <div className="d-flex" style={{ gap: "4px" }}>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => {
                EnableAppointmentModal(true, AppHour);
              }}
              title="Add Appointment"
            >
              <Icon>add</Icon>
            </button>
            <button
              className="btn btn-sm btn-info"
              onClick={() => {
                handleAddMessage();
              }}
              title="Add Message"
            >
              <Icon>chat</Icon>
            </button>
          </div>
        )}
        <span className="badge-sm">Id: {AppHour.id}</span>

        <button
          className="btn btn-sm btn-dark"
          onClick={() => {
            setShowReminders(!ShowReminders);
          }}
        >
          <Icon>?</Icon>
        </button>

        <button
          className="btn btn-sm btn-primary"
          onClick={() => {
            PasteSession(AppHour);
          }}
        >
          <Icon>paste</Icon>
        </button>
      </div>
      {ShowReminders && (
        <div
          style={{
            marginTop: "1em",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {GetReminders()}
        </div>
      )}
    </MDBox>
  );
};

export default AppHourCell2;
